import React, { useEffect, useState, useRef } from "react";
import "./marketWatch.css";
import { fetchMcpWtDavg, fetchMcpDamg } from './api';
import * as d3 from "d3";
import Charts from "./charts";
import BarCharts from "./barChart";
import GroupedBarCharts from "./groupedBarChart";

import Table from "./table";
import { IoMdArrowDropdown, IoMdArrowDropup } from "react-icons/io";

const WeightedAvgChart = () => {

    const getTodayInIST = () => {
        const now = new Date();
        const istOffset = 5.5 * 60 * 60 * 1000;
        const istDate = new Date(now.getTime() + istOffset);

        const year = istDate.getFullYear();
        const month = String(istDate.getMonth() + 1).padStart(2, "0");
        const day = String(istDate.getDate()).padStart(2, "0");

        return `${year}-${month}-${day}`;
    };
    const [selectedOption, setSelectedOption] = useState("Historical Data view");
    const [timeRange, setTimeRange] = useState(
        selectedOption === "Summary Data View" ? "30D" : "24H"
    );
    const [selectedDate, setSelectedDate] = useState(getTodayInIST());

    const [xData, setXData] = useState([]);
    const [yData, setYData] = useState([]);
    const chartRef = useRef(null);
    const tableRef = useRef(null);
    const [barXData, setBarXData] = useState([]);
    const [barYData, setBarYData] = useState([]);
    const [barAvgXData, setBarAvgXData] = useState([]);
    const [barAvgYData, setBarAvgYData] = useState([]);
    const [barTableRows, setTableData] = useState([]);
    const [barTableHeaders, setHeaders] = useState([]);
    const [barTableAvgRows, setTableAvgData] = useState([]);
    const [barTableAvgHeaders, setAvgHeaders] = useState([]);
    const [barDiffXData, setBarDiffXData] = useState([]);
    const [barDiffYData, setBarDiffYData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openDropdown, setOpenDropdown] = useState(null);
    const options = ["Historical Data view", "Summary Data View"];

    const fetchData = async (fromDate, toDate) => {
        setLoading(true);
        setXData([]);
        setYData([]);
        setBarXData([]);
        setBarYData([]);
        setBarAvgXData([]);
        setAvgHeaders([]);
        setBarAvgYData([]);
        setTableAvgData([]);
        setBarDiffXData([]);
        setBarDiffYData([]);
        setTableData([]);
        setHeaders([]);
        try {
            const [wtdavgRes, damgRes] = await Promise.all([
                fetchMcpWtDavg(fromDate, toDate),
                fetchMcpDamg(fromDate, toDate)
            ]);
            //  prepareChartData(damgRes.data);
            prepareLineChartDataForAverage(damgRes.data);
            prepareBarChartDataFromHistorical(wtdavgRes.data);
            prepareBarChartData(damgRes.data);
        } catch (err) {
            console.error("Error fetching data:", err);
        } finally {
            setLoading(false);
        }
    };

    const toggleDropdown = (type) => {
        setOpenDropdown(openDropdown === type ? null : type);
    };

    const handleSelect = (option) => {
        setSelectedOption(option);
        setOpenDropdown(null);

        if (option === "Historical Data view") {
            handleRangeChange("24H");
        } else if (option === "Summary Data View") {
            handleRangeChange("30D");
        }
    };


    const handleRangeChange = (range) => {
        setTimeRange(range);
        const today = new Date();
        let fromDate, toDate;

        if (range === "24H") {
            const today = new Date();
            const year = today.getFullYear();
            const month = String(today.getMonth() + 1).padStart(2, "0");
            const day = String(today.getDate()).padStart(2, "0");

            const formattedForPicker = `${year}-${month}-${day}`;
            setSelectedDate(formattedForPicker);

            const todayISO = today.toISOString().split("T")[0];
            fromDate = todayISO;
            toDate = todayISO;
        } else if (range === "7D") {
            const pastDate = new Date(today);
            pastDate.setDate(today.getDate() - 6);
            fromDate = pastDate.toISOString().split("T")[0];
            toDate = today.toISOString().split("T")[0];
        } else if (range === "30D") {
            const pastDate = new Date(today);
            pastDate.setDate(today.getDate() - 29);
            fromDate = pastDate.toISOString().split("T")[0];
            toDate = today.toISOString().split("T")[0];
        }

        fetchData(fromDate, toDate);
    };

    const handleDateChange = (date) => {
        setSelectedDate(date);
        setTimeRange("24H");
        fetchData(date, date);
    };

    useEffect(() => {
        fetchData(selectedDate, selectedDate);
    }, []);

    const sortByDateAndTime = (data) => {
        return [...data].sort((a, b) => {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);

            if (dateA.getTime() !== dateB.getTime()) {
                return dateA - dateB;
            }

            const timeA = a.time_block.split(" - ")[0].trim();
            const timeB = b.time_block.split(" - ")[0].trim();

            return timeA.localeCompare(timeB);
        });
    };

    const prepareChartData = (data) => {
        if (!data || data.length === 0) return;

        const sortedData = sortByDateAndTime(data);

        const xValues = sortedData.map(row => {
            const dateObj = new Date(row.date);
            return dateObj.toLocaleDateString("en-US", { month: "short", day: "2-digit" });
        });

        const numericFields = ["dam", "gdam", "rtm"];
        const yValues = numericFields.map((field, index) => ({
            field,
            values: sortedData.map(row => parseFloat(row[field]) || 0),
            color: d3.schemeCategory10[index % 10],
        }));

        setXData(xValues);
        setYData(yValues);
    };

    const prepareLineChartDataForAverage = (data) => {
        if (!data || data.length === 0) return;

        const sortedData = sortByDateAndTime(data);

        const uniqueDates = [...new Set(sortedData.map(row => row.date))];
        const multipleDates = uniqueDates.length > 1;

        const chartFields = ["dam_mcp", "gdam_mcp", "rtm_mcp"];
        const yValues = chartFields.map((field, index) => ({
            field,
            values: sortedData.map(row => parseFloat(row[field]) || 0),
            color: d3.schemeCategory10[index % 10],
        }));

        const xValues = sortedData.map(row => {
            const timeStart = row.time_block.split(" - ")[0].trim();
            if (multipleDates) {
                const datePart = new Date(row.date).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                });
                return `${datePart} - ${timeStart}`;
            }
            return timeStart;
        });

        setXData(xValues);
        setYData(yValues);
    };


    const prepareBarChartDataFromHistorical = (data) => {
        if (!data || data.length === 0) return;

        const sortedData = [...data].sort((a, b) => new Date(a.date) - new Date(b.date));

        const xValues = sortedData.map(row => {
            const datePart = new Date(row.date).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
            });
            return datePart;
        });

        // Original numeric fields for first bar chart
        const numericFields = ["dam", "gdam", "rtm"];
        const yValues = numericFields.map((field, index) => ({
            field,
            values: sortedData.map(row => parseFloat(row[field]) || 0),
            color: d3.schemeCategory10[index % 10],
        }));

        setBarAvgXData(xValues);
        setBarAvgYData(yValues);

        // Fields coming from API for second bar chart
        const diffFields = ["dam_gdam", "dam_rtm"];
        const yDiffValues = diffFields.map((field, index) => ({
            field,
            values: sortedData.map(row => parseFloat(row[field]) || 0),
            color: d3.schemeCategory10[(numericFields.length + index) % 10],
        }));

        setBarDiffXData(xValues);
        setBarDiffYData(yDiffValues);

        // Table data
        const tableRows = xValues.map((xVal, idx) => {
            const row = sortedData[idx];
            return {
                Date: xVal,
                dam: parseFloat(row.dam) || 0,
                gdam: parseFloat(row.gdam) || 0,
                rtm: parseFloat(row.rtm) || 0,
                dam_gdam: parseFloat(row.dam_gdam) || 0,
                dam_rtm: parseFloat(row.dam_rtm) || 0,
            };
        });

        const tableHeaders = [
            { name: "Date", dataType: "text", editable: false, width: 160, displayName: "Date" },
            ...numericFields.map(field => ({
                name: field,
                dataType: "number",
                editable: true,
                width: 120,
                displayName: field.replace(/_/g, " ").toUpperCase(),
            })),
            ...diffFields.map(field => ({
                name: field,
                dataType: "number",
                editable: false,
                width: 140,
                displayName: field.replace(/_/g, " ").toUpperCase(),
            })),
        ];

        setTableAvgData(tableRows);
        setAvgHeaders(tableHeaders);
    };

    const prepareBarChartData = (data) => {
        if (!data || data.length === 0) return;

        const sortedData = sortByDateAndTime(data);

        const uniqueDates = [...new Set(sortedData.map(row => row.date))];
        const multipleDates = uniqueDates.length > 1;

        const chartFields = ["dam_gdam", "dam_rtm"];
        const yValues = chartFields.map((field, index) => ({
            field,
            values: sortedData.map(row => parseFloat(row[field]) || 0),
            color: d3.schemeCategory10[index % 10],
        }));

        const xValues = sortedData.map(row => {
            const timeStart = row.time_block.split(" - ")[0].trim();
            if (multipleDates) {
                const datePart = new Date(row.date).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                });
                return `${datePart} - ${timeStart}`;
            }
            return timeStart;
        });

        setBarXData(xValues);
        setBarYData(yValues);

        const tableFields = ["dam_mcp", "gdam_mcp", "rtm_mcp", "dam_gdam", "dam_rtm"];
        const tableRows = xValues.map((xVal, idx) => {
            const row = { Time: xVal };
            tableFields.forEach(field => {
                row[field] = parseFloat(sortedData[idx][field]) || 0;
            });
            return row;
        });

        const tableHeaders = [
            {
                name: "Time",
                dataType: "text",
                editable: false,
                width: 160,
                displayName: multipleDates ? "Date - Time" : "Time",
            },
            ...tableFields.map(field => ({
                name: field,
                dataType: "number",
                editable: true,
                width: 120,
                displayName: field.replace(/_/g, " ").toUpperCase(),
            }))
        ];

        setTableData(tableRows);
        setHeaders(tableHeaders);
    };


    return (
        <div>
            {loading && (
                <div className="dashboard-loading-bar"></div>
            )}
            <div className="header-container">
                <div className="header-title">
                    {selectedOption === "Summary Data View"
                        ? "Summary Data View"
                        : "Historical Data View"}
                </div>
                <div className="time-range-tabs">
                    {["24H", "7D", "30D"].map(range => (
                        <button
                            key={range}
                            className={timeRange === range ? "active" : ""}
                            onClick={() => handleRangeChange(range)}
                        >
                            {range}
                        </button>
                    ))}
                    <div className="date-picker">
                        <input
                            id="date-input"
                            type="date"
                            value={selectedDate}
                            onChange={(e) => handleDateChange(e.target.value)}
                            placeholder=" "
                        />
                        <label htmlFor="date-input">Date</label>
                    </div>

                </div>
                <div className="dropdown-container">
                    <button onClick={() => toggleDropdown("market")} className="dropdown-button">
                        {selectedOption} {openDropdown === "market" ? <IoMdArrowDropup /> : <IoMdArrowDropdown />}
                    </button>

                    {openDropdown === "market" && (
                        <div className="dropdown-menus">
                            {options.map((option, index) => (
                                <div
                                    key={index}
                                    onClick={() => handleSelect(option)}
                                    className={`dropdown-item ${selectedOption === option ? "selected" : ""}`}
                                >
                                    {option}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            {selectedOption === "Historical Data view" && (
                <>
                    {yData.length > 0 && (
                        <>
                            <h4>Price Comparision</h4>
                            <p>DAM Vs GDAM Vs RTM</p>
                            <div className="charts-container">
                                <Charts
                                    ref={chartRef}
                                    xData={xData}
                                    yData={yData}
                                    drag={false}
                                    edit={false}
                                />
                            </div>
                        </>
                    )}
                    {barYData.length > 0 && (
                        <>
                            <h4>MCP Price Comparision</h4>
                            <div className="charts-container">
                                <BarCharts
                                    xData={barXData}
                                    yData={barYData}
                                />
                            </div>
                        </>
                    )}
                    {barTableRows.length > 0 && barTableHeaders.length > 0 && (
                        <>
                            <h4>Detailed Data</h4>
                            <Table
                                ref={tableRef}
                                headers={barTableHeaders}
                                 maxHeight="550px"
                                rows={barTableRows}
                                edit={false}
                                sorting={true}
                                drag={false}
                                filtering={true}
                                exporting={true}
                                addRow={false}
                                restrictEditToNewRows={false}
                            />
                        </>
                    )}
                </>
            )}

            {selectedOption === "Summary Data View" && (
                <>
                    {barAvgYData.length > 0 && (
                        <>
                            <h4>Price Comparison</h4>
                            <p>DAM Vs GDAM Vs RTM</p>
                            <div className="charts-container">
                                <GroupedBarCharts
                                    xData={barAvgXData}
                                    yData={barAvgYData}
                                />
                            </div>
                        </>
                    )}
                    {barDiffXData.length > 0 && (
                        <>
                            <h4>MCP Price Comparision</h4>
                            <div className="charts-container">
                                <GroupedBarCharts
                                    xData={barDiffXData}
                                    yData={barDiffYData}
                                />
                            </div>
                        </>
                    )}
                    {barTableAvgRows.length > 0 && barTableAvgHeaders.length > 0 && (
                        <>
                            <h4>Detailed Data</h4>
                            <Table
                                ref={tableRef}
                                headers={barTableAvgHeaders}
                                 maxHeight="550px"
                                rows={barTableAvgRows}
                                edit={false}
                                sorting={true}
                                drag={false}
                                filtering={true}
                                exporting={true}
                                addRow={false}
                                restrictEditToNewRows={false}
                            />
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default WeightedAvgChart;
